import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { TabType } from 'types/TabTypes';
import { useTranslation } from 'react-i18next';
import Timeline from 'components/timeline/Timeline';
import PdfLayoutExample from '../../views/settings/pdf/PdfLayoutExample';
import JobDetailList from '../../views/jobs/JobDetailList';
import AttachmentDetailList from '../../views/attachment/AttachmentDetailList';
import ProposalDetailList from '../../views/proposals/ProposalDetailList';
import ProjectTimeRowTab from '../../views/jobs/ProjectTimeRowTab';
import { EditableStatusType } from 'types/FieldTypes';
import ProjectPlanningTab from '../../views/jobs/ProjectPlanningTab';
import ProjectJobsTab from '../../views/jobs/ProjectJobsTab';

interface UseTabBarProps {
    tabs: TabType[];
    data?: any;
    viewKey?: string;
    itemId?: string;
    pageContext?: string;
    reloadTabs?: number;
    editable?: EditableStatusType;
    extraProps?: Record<string, any>;
    basePath?: string;
    syncWithUrl?: boolean;
}

export const useTabBar = ({
    tabs,
    data,
    viewKey,
    itemId,
    pageContext,
    reloadTabs,
    editable,
    extraProps,
    basePath,
    syncWithUrl = false,
}: UseTabBarProps) => {
    const { t } = useTranslation();
    const location = useLocation();
    const history = useHistory();

    const requestedTabPath = useMemo(() => {
        if (syncWithUrl && basePath && location.pathname.startsWith(basePath)) {
            return location.pathname
                .slice(basePath.length)
                .replace(/^\/+|\/+$/g, '');
        }

        return '';
    }, [basePath, location.pathname, syncWithUrl]);

    const getTabIndexFromPath = () => {
        const requestedTabIndex = tabs.findIndex(tab => tab.path === requestedTabPath);

        return requestedTabIndex >= 0 ? requestedTabIndex : 0;
    };

    const [activeTab, setActiveTab] = useState(getTabIndexFromPath);

    useEffect(() => {
        const nextActiveTab = getTabIndexFromPath();

        if (activeTab !== nextActiveTab) {
            setActiveTab(nextActiveTab);
        }

        if (
            syncWithUrl &&
            basePath &&
            tabs.length > 1 &&
            tabs[0]?.path &&
            requestedTabPath !== tabs[nextActiveTab]?.path
        ) {
            history.replace(`${basePath}/${tabs[nextActiveTab].path}`);
        }
    }, [
        activeTab,
        basePath,
        history,
        requestedTabPath,
        syncWithUrl,
        tabs,
    ]);

    const handleTabChange = (tab: TabType, index: number) => {
        setActiveTab(index);

        if (syncWithUrl && basePath && tab.path) {
            history.push(`${basePath}/${tab.path}`);
        }
    };

    const tabComponents = tabs.map((tab, index) => {
        let component = tab.component;

        if (!component && tab.type && viewKey) {
            switch (tab.type) {
                case 'timeline':
                    component = itemId && pageContext ? (
                        <Timeline
                            key={index}
                            placement='page'
                            timeline_page={pageContext}
                            timeline_page_id={parseInt(itemId)}
                            showOnlyTimelineUpdates={tab.showOnlyTimelineUpdates}
                            data={data}
                        />
                    ) : null;
                    break;

                case 'proposals':
                    component = itemId && pageContext ? (
                        <ProposalDetailList
                            key={index}
                            itemId={itemId}
                            detailPage={pageContext}
                            data={data}
                        />
                    ) : null;
                    break;

                case 'jobs':
                    component = itemId && pageContext ? (
                        <JobDetailList
                            key={index}
                            itemId={itemId}
                            detailPage={pageContext}
                            data={data}
                        />
                    ) : null;
                    break;

                case 'pdf-layout':
                    component = itemId ? (
                        <PdfLayoutExample
                            itemId={itemId}
                            reloadPdf={reloadTabs || null}
                        />
                    ) : null;
                    break;

                case 'attachments':
                    component = itemId && pageContext ? (
                        <AttachmentDetailList
                            key={index}
                            detailPageId={itemId}
                            detailPage={pageContext}
                        />
                    ) : null;
                    break;

                case 'project-jobs':
                    component = itemId && data?.jobs ? (
                        <ProjectJobsTab
                            key={index}
                            project={data}
                            isEditable={editable}
                        />
                    ) : null;
                    break;

                case 'project-planning':
                    component = itemId && data?.jobs ? (
                        <ProjectPlanningTab
                            key={index}
                            viewKey={viewKey}
                            project={data}
                            isEditable={editable}
                            weekStartDate={extraProps?.schedulerWeekStart}
                        />
                    ) : null;
                    break;

                case 'project-time-rows':
                    component = itemId ? (
                        <ProjectTimeRowTab
                            key={index}
                            data={data}
                        />
                    ) : null;
                    break;
            }
        }

        const label = (
            <li
                key={tab.path || index}
                onClick={() => handleTabChange(tab, index)}
                className={index === activeTab ? 'active' : ''}
            >
                {t(tab.label || '')}
            </li>
        );

        return {
            label,
            component: index === activeTab ? component : null,
        };
    });

    return {
        tabLabel: tabComponents.map(tab => tab.label),
        tabComponent: tabComponents.map(tab => tab.component),
        activeTab,
    };
};